import { BarChart3, Target, TrendingUp } from "lucide-react";
import { useState, useEffect } from "react";

const HOME_COLOR = "#22D3EE";   // Cyan brillante
const AWAY_COLOR = "#FBBF24";   // Amber vibrante
const TRACK_BG   = "var(--bc-panel-elev, rgba(255,255,255,0.03))";
const TRACK_BORDER = "var(--bc-border, rgba(255,255,255,0.07))";
const MUTED = "#4B5563";
const TEXT_DIM = "#6B7280";

const VARIANT_CONFIG = {
  real: {
    accent: "#22C55E",
    label: "STATISTICHE PARTITA",
    badge: null,
    note: null,
    Icon: BarChart3,
  },
  predicted: {
    accent: "#A78BFA",
    label: "STATISTICHE PREVISTE",
    badge: "DELTA · ULTIME 10",
    note: "Modello delta-weighted: medie reali ultime 10 partite (ponderate per recency) confrontate tra attacco casa e difesa ospiti",
    Icon: Target,
  },
  season: {
    accent: "#F97316",
    label: "MEDIE STAGIONALI",
    badge: "BASELINE XG",
    note: "Media attesa per una partita tipo nella stagione corrente",
    Icon: TrendingUp,
  },
};

const METRICS = [
  { key: "xg",              label: "xG Attesi",         fmt: (v) => v != null ? (typeof v === "number" ? v.toFixed(2) : String(v)) : "—" },
  { key: "total_shots",     label: "Tiri Totali",        fmt: (v) => v != null ? (typeof v === "number" ? v.toFixed(1) : String(v)) : "—" },
  { key: "shots_on_target", label: "Tiri in Porta",      fmt: (v) => v != null ? (typeof v === "number" ? v.toFixed(1) : String(v)) : "—" },
  { key: "possession",      label: "Possesso",           fmt: (v) => { if (v == null) return "—"; return String(v).includes("%") ? String(v) : `${v}%`; }, isPossession: true },
  { key: "corners",         label: "Calci d'Angolo",     fmt: (v) => v != null ? (typeof v === "number" ? v.toFixed(1) : String(v)) : "—" },
  { key: "fouls",           label: "Falli",              fmt: (v) => v != null ? (typeof v === "number" ? v.toFixed(1) : String(v)) : "—" },
  { key: "offsides",        label: "Fuorigioco",         fmt: (v) => v != null ? (typeof v === "number" ? v.toFixed(1) : String(v)) : "—" },
  { key: "yellow_cards",    label: "Cartellini Gialli",  fmt: (v) => v != null ? (typeof v === "number" ? v.toFixed(1) : String(v)) : "—" },
];

function parsePossession(v) {
  if (v == null) return 50;
  if (typeof v === "string") return parseInt(v.replace("%", ""), 10) || 50;
  return Number(v) || 50;
}

function getBarPcts(metric, hVal, aVal) {
  if (metric.isPossession) {
    const h = parsePossession(hVal);
    return { homePct: Math.min(100, Math.max(0, h)), awayPct: Math.min(100, Math.max(0, 100 - h)) };
  }
  const h = parseFloat(hVal) || 0;
  const a = parseFloat(aVal) || 0;
  const total = h + a;
  if (total === 0) return { homePct: 50, awayPct: 50 };
  const hp = Math.round((h / total) * 100);
  return { homePct: hp, awayPct: 100 - hp };
}

/* ── Glow bar: il vero boost visivo ── */
function GlowBar({ pct, color, side, animated, isLeader }) {
  const isLeft = side === "home";
  const direction = isLeft ? "to right" : "to left";

  // Leading bar: gradiente saturo con glow doppio
  // Non-leading: desaturato e opaco
  const barGradient = isLeader
    ? `linear-gradient(${direction}, ${color}28 0%, ${color}70 40%, ${color} 85%, ${color}FF 100%)`
    : `linear-gradient(${direction}, ${color}10 0%, ${color}38 100%)`;

  const glowShadow = isLeader
    ? `0 0 18px ${color}80, 0 0 36px ${color}35`
    : "none";

  const radius = isLeft ? "3px 0 0 3px" : "0 3px 3px 0";
  const translateDir = isLeft ? "translateX(-4px)" : "translateX(4px)";

  return (
    <div
      className={isLeader ? "bc-glow-bar-leader" : ""}
      style={{
        width: animated ? `${pct}%` : "0%",
        height: "100%",
        position: "relative",
        background: barGradient,
        boxShadow: glowShadow,
        borderRadius: radius,
        transition: `width 0.85s cubic-bezier(0.22, 1, 0.36, 1)`,
        overflow: "hidden",
        transform: animated ? "none" : translateDir,
      }}
    >
      {/* Shimmer di luce in cima alla barra (solo leader) */}
      {isLeader && (
        <div
          style={{
            position: "absolute",
            top: 0, left: 0, right: 0,
            height: "45%",
            background: "linear-gradient(to bottom, rgba(255,255,255,0.28) 0%, transparent 100%)",
            borderRadius: radius,
            pointerEvents: "none",
          }}
        />
      )}

      {/* Continuous scan light — only on leader, loops slowly so the bar
          is visually "alive" even after fill animation completes. */}
      {isLeader && (
        <div className="bc-leader-scan" style={{
          position: "absolute", top: 0, bottom: 0, width: "30%",
          background: `linear-gradient(${isLeft ? "90deg" : "270deg"}, transparent, rgba(255,255,255,0.55), transparent)`,
          pointerEvents: "none", mixBlendMode: "screen",
        }} />
      )}

      {/* Pulse orizzontale al bordo del leader (spark di intensità) */}
      {isLeader && pct > 10 && (
        <div
          style={{
            position: "absolute",
            top: 0,
            [isLeft ? "right" : "left"]: 0,
            width: 3,
            height: "100%",
            background: `linear-gradient(to bottom, ${color}FF, ${color}80, ${color}FF)`,
            boxShadow: `0 0 6px ${color}`,
            borderRadius: isLeft ? "0 3px 3px 0" : "3px 0 0 3px",
          }}
        />
      )}
    </div>
  );
}

export default function StatsComparisonPanel({ home, away, homeName, awayName, variant = "predicted" }) {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 120);
    return () => clearTimeout(timer);
  }, []);

  if (!home || !away) return null;

  const cfg = VARIANT_CONFIG[variant] || VARIANT_CONFIG.predicted;
  const { accent, label, badge, note, Icon } = cfg;

  return (
    <div
      data-testid={`stats-panel-${variant}`}
      style={{
        background: "#0A0A0F",
        border: `1px solid ${TRACK_BORDER}`,
        borderTop: `2px solid ${accent}`,
        overflow: "hidden",
      }}
    >
      {/* ── Header ── */}
      <div
        className="px-4 py-3 flex items-center justify-between"
        style={{
          borderBottom: `1px solid ${TRACK_BORDER}`,
          background: `linear-gradient(90deg, ${accent}10 0%, transparent 55%)`,
        }}
      >
        <div className="flex items-center gap-2">
          <Icon size={14} style={{ color: accent }} />
          <span className="font-heading font-black text-xs uppercase tracking-[0.2em]" style={{ color: accent }}>
            {label}
          </span>
          {badge && (
            <span
              className="font-data text-[10px] px-2 py-0.5 uppercase tracking-wider"
              style={{ color: accent, background: `${accent}14`, border: `1px solid ${accent}33` }}
            >
              {badge}
            </span>
          )}
        </div>

        {/* Team legend */}
        <div className="hidden sm:flex items-center gap-4">
          {[{ color: HOME_COLOR, name: homeName }, { color: AWAY_COLOR, name: awayName }].map(({ color, name }) => (
            <div key={name} className="flex items-center gap-1.5">
              <div style={{
                width: 8, height: 8, background: color, borderRadius: 2,
                boxShadow: `0 0 6px ${color}90`,
              }} />
              <span className="font-heading text-[10px] uppercase tracking-wide" style={{ color }}>
                {name}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Metrics rows ── */}
      <div className="px-5 py-5 space-y-5">
        {METRICS.map((metric) => {
          const hVal = home[metric.key];
          const aVal = away[metric.key];
          if (hVal == null && aVal == null) return null;

          const { homePct, awayPct } = getBarPcts(metric, hVal, aVal);
          const hFormatted = metric.fmt(hVal);
          const aFormatted = metric.fmt(aVal);
          const homeLeads = homePct > awayPct;
          const awayLeads = awayPct > homePct;
          const tied = !homeLeads && !awayLeads;

          return (
            <div key={metric.key} data-testid={`stat-row-${metric.key}`}>

              {/* Values + label */}
              <div className="flex items-baseline justify-between mb-2">
                {/* Home value */}
                <span
                  style={{
                    fontVariantNumeric: "tabular-nums",
                    fontWeight: 900,
                    fontFamily: "'Space Grotesk', monospace",
                    fontSize: homeLeads ? 17 : 13,
                    color: homeLeads ? HOME_COLOR : `${HOME_COLOR}55`,
                    textShadow: homeLeads ? `0 0 14px ${HOME_COLOR}90, 0 0 28px ${HOME_COLOR}40` : "none",
                    transition: "font-size 0.3s, color 0.3s",
                    minWidth: 38,
                  }}
                >
                  {hFormatted}
                </span>

                {/* Metric label */}
                <span
                  className="font-data text-[10px] uppercase tracking-[0.15em]"
                  style={{ color: TEXT_DIM, letterSpacing: "0.13em" }}
                >
                  {metric.label}
                </span>

                {/* Away value */}
                <span
                  style={{
                    fontVariantNumeric: "tabular-nums",
                    fontWeight: 900,
                    fontFamily: "'Space Grotesk', monospace",
                    fontSize: awayLeads ? 17 : 13,
                    color: awayLeads ? AWAY_COLOR : `${AWAY_COLOR}55`,
                    textShadow: awayLeads ? `0 0 14px ${AWAY_COLOR}90, 0 0 28px ${AWAY_COLOR}40` : "none",
                    transition: "font-size 0.3s, color 0.3s",
                    textAlign: "right",
                    minWidth: 38,
                  }}
                >
                  {aFormatted}
                </span>
              </div>

              {/* ── Neon Glow Split Bar ── */}
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  height: 10,
                  borderRadius: 4,
                  background: TRACK_BG,
                  border: `1px solid ${TRACK_BORDER}`,
                  overflow: "hidden",
                }}
              >
                {/* Home bar */}
                <GlowBar
                  pct={homePct}
                  color={HOME_COLOR}
                  side="home"
                  animated={animated}
                  isLeader={homeLeads || tied}
                />

                {/* Away bar */}
                <GlowBar
                  pct={awayPct}
                  color={AWAY_COLOR}
                  side="away"
                  animated={animated}
                  isLeader={awayLeads || tied}
                />

                {/* Marker 50% al centro */}
                <div
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: 0, bottom: 0,
                    width: 1,
                    background: "var(--bc-border, rgba(255,255,255,0.1))",
                    zIndex: 10,
                    transform: "translateX(-0.5px)",
                    pointerEvents: "none",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer note */}
      {note && (
        <div className="px-5 py-2.5" style={{ borderTop: `1px solid ${TRACK_BORDER}` }}>
          <p className="font-data text-[10px] leading-relaxed" style={{ color: MUTED }}>
            ⓘ {note}
          </p>
        </div>
      )}
    </div>
  );
}
